import {
  snackbarContentClasses,
  type SnackbarContentProps as MuiSnackbarContentProps,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';

const SEVERITIES = ['default', 'success', 'warning', 'error'] as const;

declare module '@mui/material/SnackbarContent' {
  interface SnackbarContentProps {
    severity?: 'default' | 'success' | 'warning' | 'error';
    variant?: 'filled' | 'standard' | 'outlined';
  }
}

export default function Snackbar(theme: Theme) {
  const rootStyles = (ownerState: MuiSnackbarContentProps) => {
    const variant = ownerState.variant ?? 'filled';
    const filled = variant === 'filled';
    const standard = variant === 'standard';
    const outlined = variant === 'outlined';

    const baseStyle = {
      width: 320,
      maxWidth: '100%',
      borderRadius: theme.spacing(0.5),
      padding: theme.spacing(1.5),
      boxShadow: 'none',
      flexWrap: 'nowrap' as const,
      [`& .${snackbarContentClasses.message}`]: {
        padding: 0,
        flex: 1,
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'flex-start',
      },
      [`& .${snackbarContentClasses.action}`]: {
        padding: 0,
        marginRight: 0,
        marginLeft: theme.spacing(2),
        alignSelf: 'flex-start',
        paddingTop: theme.spacing(0.25),
      },
    };

    const colorStyle = SEVERITIES.map((color) => ({
      ...(ownerState.severity === color && {
        ...(filled &&
          color === 'default' && {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.grey[700],
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: theme.customShadows.z1,
          }),
        ...(filled &&
          color !== 'default' && {
            backgroundColor: theme.palette[color][600],
            color: theme.palette[color].contrastText,
          }),
        ...(standard &&
          color === 'default' && {
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.grey[700],
          }),
        ...(standard &&
          color !== 'default' && {
            backgroundColor:
              theme.palette[color][color === 'success' ? 50 : 100],
            color: theme.palette[color][600],
          }),
        ...(outlined &&
          color === 'default' && {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.grey[700],
            border: `1px solid ${theme.palette.grey[300]}`,
          }),
        ...(outlined &&
          color !== 'default' && {
            backgroundColor: theme.palette.common.white,
            color: theme.palette[color][600],
            border: `1px solid ${theme.palette[color][300]}`,
          }),
      }),
    }));

    return [baseStyle, ...colorStyle];
  };

  return {
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: {
          vertical: 'top' as const,
          horizontal: 'right' as const,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: MuiSnackbarContentProps }) =>
          rootStyles(ownerState),
      },
    },
  };
}
